import React, { useState } from 'react';

import Button from 'CommonComponents/Button';
import FormInput from 'CommonComponents/FormInput';

/**
 * A session type's name and whether its sessions award medals, with Save,
 * Cancel and, for an existing type, Delete. onSubmit and onDelete return
 * promises; a validation rejection is shown under the name.
 */
export default function SessionTypeForm({ id, initial = { name: '', is_medal: false }, onSubmit, onCancel, onDelete, autoFocus = true }) {
    const [name, setName] = useState(initial.name);
    const [isMedal, setIsMedal] = useState(!!initial.is_medal);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const run = (action) => {
        setBusy(true);
        setError('');

        action()
            .catch((err) => {
                const errors = err?.response?.data?.errors;
                if (errors) {
                    setError(Object.values(errors).flat().join(' '));
                }
            })
            .finally(() => setBusy(false));
    };

    const submit = (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setError($t('Name is required'));

            return;
        }

        run(() => onSubmit({ name: name.trim(), is_medal: isMedal }));
    };

    return (
        <form
            className="tw-flex tw-flex-wrap tw-items-start tw-gap-2 tw-w-full tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-charcoal-15 tw-bg-background"
            onSubmit={submit}
        >
            <div className="tw-flex-1 tw-min-w-[12rem]">
                <FormInput
                    id={`${id}-name`}
                    name={`${id}-name`}
                    type="text"
                    label={$t('Session type name')}
                    hideLabel={true}
                    aria-label={$t('Session type name')}
                    placeholder={$t('Session type name')}
                    maxLength={191}
                    autoFocus={autoFocus}
                    value={name}
                    readOnly={busy}
                    showError={!!error}
                    errorText={error}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancel();
                        }
                    }}
                    onChange={(e) => {
                        setError('');
                        setName(e.target.value);
                    }}
                />
            </div>
            <label htmlFor={`${id}-medal`} className="tw-flex tw-items-center tw-gap-x-2 tw-h-8.5 tw-mb-0 tw-text-sm tw-text-charcoal">
                <input id={`${id}-medal`} type="checkbox" checked={isMedal} disabled={busy} onChange={(e) => setIsMedal(e.target.checked)} />
                {$t('Medal session')}
            </label>
            <Button type="submit" variant="primary" small loading={busy} disabled={busy}>
                {$t('Save')}
            </Button>
            <Button variant="outline" small disabled={busy} onClick={onCancel}>
                {$t('Cancel')}
            </Button>
            {onDelete && (
                <Button variant="outline-danger" small disabled={busy} onClick={() => run(onDelete)}>
                    {$t('Delete')}
                </Button>
            )}
        </form>
    );
}
